import { Event } from "../../Global/Base/Events";
import { GlobalContext } from "../../Global/Base/GlobalContext";

const savedPasswordsSymbol = Symbol.for("savedPasswords");
const savedTemporaryPasswordsSymbol = Symbol.for("savedTemporaryPasswords");

// internal veci pro PasswordManager
class savedPasswords {
    private static _passwords: Map<string, CryptoKey>;

    /**
    * Hesla k novým záznamům (které ještě nejsou uložené a nemají vlastní ID)
    */
    private static _temporaryPasswords: Map<string, CryptoKey>;

    public static get passwords(): Map<string, CryptoKey> {
        if (!savedPasswords._passwords) {
            const istMgr: any = self !== top ? GlobalContext.getType<typeof PasswordManager>(PasswordManager) : null;
            if (istMgr && istMgr[savedPasswordsSymbol])
                savedPasswords._passwords = istMgr[savedPasswordsSymbol];
            else
                savedPasswords._passwords = new Map();
        }
        return savedPasswords._passwords;
    }

    public static get temporaryPasswords(): Map<string, CryptoKey> {
        if (!savedPasswords._temporaryPasswords) {
            const istMgr: any = self !== top ? GlobalContext.getType<typeof PasswordManager>(PasswordManager) : null;
            if (istMgr && istMgr[savedTemporaryPasswordsSymbol])
                savedPasswords._temporaryPasswords = istMgr[savedTemporaryPasswordsSymbol];
            else
                savedPasswords._temporaryPasswords = new Map();
        }
        return savedPasswords._temporaryPasswords;
    }
}

export class PasswordManager {
    /**
     * Událost vyvolaná při přidání nového hesla do password manageru
     */
    public static addedPassword: Event<{ id: string; className: string }, undefined> = new Event<{ id: string; className: string }, undefined>();

    // @ts-ignore // nevyužito, ale ponecháno
    private static get [savedPasswordsSymbol](): Map<string, CryptoKey> {
        return savedPasswords.passwords;
    }

    // @ts-ignore // nevyužito, ale ponecháno
    private static get [savedTemporaryPasswordsSymbol](): Map<string, CryptoKey> {
        return savedPasswords.temporaryPasswords;
    }

    /**
     * Značí, zda v úložišti hesel existuje pro danou třídu a id nějaké heslo
     * @param className Název třídy objektu
     * @param id ID objektu
     */
    public static hasPassword(className: string, id: string): boolean {
        return PasswordManager.getPassword(className, id) != null;
    }

    /**
     * Značí, zda v úložišti hesel existuje pro danou neuloženou instanci heslo
     * @param instanceID InstanceID objektu, pro který se ověřuje existence hesla
     */
    public static hasTempPassword(instanceID: string): boolean {
        return PasswordManager.getTempPassword(instanceID) != null;
    }

    /**
     * Vrací heslo z úložiště hesel pro danou třídu a id (null, pokud nebylo nalezeno)
     * @param className Název třídy objektu
     * @param id ID objektu
     */
    public static getPassword(className: string, id: string): CryptoKey | undefined {
        const identificator = PasswordManager._createObjectIdentification(className, id);
        return savedPasswords.passwords.get(identificator);
    }

    /**
     * Vrací heslo z úložiště hesel pro danou třídu a id (null, pokud nebylo nalezeno)
     * @param instanceId Id instance objektu, pro který se hledá heslo
     */
    public static getTempPassword(instanceId: string): CryptoKey | undefined {
        return savedPasswords.temporaryPasswords.get(instanceId);
    }

    /**
     * Uloží do úložiště hesel nové heslo pro danou třídu a id
     * @param className Název třídy objektu
     * @param id ID objektu
     * @param password Heslo k uložení
     */
    public static addPassword(className: string, id: string, password: CryptoKey, suppressNotify?: boolean): void {
        const identificator = PasswordManager._createObjectIdentification(className, id);
        savedPasswords.passwords.set(identificator, password);
        if (!suppressNotify)
            this.addedPassword.trigger({ id: id, className: className }, undefined);
    }

    /**
     * Odstraní z úložiště hesel heslo pro danou třídu a id - používá se po finálním zašifrování objektu
     * @param className Název třídy objektu
     * @param id ID objektu
     */
    public static deletePassword(className: string, id: string): void {
        const identificator = PasswordManager._createObjectIdentification(className, id);
        savedPasswords.passwords.delete(identificator);
    }

    /**
     * Uloží do úložiště hesel dočasné heslo pro neuloženou instanci
     * @param instanceId Id instance objektu, pro který se vkládá heslo
     * @param password Heslo k uložení
     */
    public static addTempPassword(instanceId: string, password: CryptoKey, suppressNotify?: boolean): void {
        savedPasswords.temporaryPasswords.set(instanceId, password);
        if (!suppressNotify)
            this.addedPassword.trigger({ id: instanceId, className: "" }, undefined);
    }

    /**
     * Odstraní heslo k objektu daného InstanceID z dočasného úložiště hesel
     * @param instanceID InstanceID objektu, u kterého se má odstranit heslo
     */
    private static _deleteTempPassword(instanceID: string): void {
        savedPasswords.temporaryPasswords.delete(instanceID);
    }

    /**
     * Přesune heslo z dočasného úložiště do trvalého
     * @param instanceID InstanceID objektu, jehož heslo se má přesunout
     * @param className Třída objektu
     * @param id ID objektu
     */
    public static movePasswordFromTemp(instanceID: string, className: string, id: string): boolean {
        const temporaryPassword = PasswordManager.getTempPassword(instanceID);
        if (temporaryPassword == null)
            return false;
        PasswordManager.addPassword(className, id, temporaryPassword);
        PasswordManager._deleteTempPassword(instanceID);

        return PasswordManager.hasPassword(className, id);
    }

    private static _createObjectIdentification(className: string, id: string): string {
        return `${className}:${id}`;
    }
}

GlobalContext.registerType(PasswordManager);
